import { Injectable } from '@angular/core';
import {debounceTime, Observable, Subject} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ErrorCountsByCategory} from '../../models/dashboard/ErrorCountsByCategory';
import {ErrorCountsByCategoryApi} from '../../api/models/dashboard/ErrorCountsByCategoryApi';
import {BaseHubService} from './base-hub.service';
import {ErrorChartState} from '../../models/dashboard/ErrorChartState';
import {ErrorChartDataApi} from '../../api/models/dashboard/ErrorChartDataApi';
import {ErrorChartData} from '../../models/dashboard/ErrorChartData';
import {ErrorChartLabel} from '../../models/dashboard/ErrorChartLabel';
import {ErrorDataset} from '../../models/dashboard/ErrorDataset';
import {WebsocketConnectionException} from '../../models/exception/WebsocketConnectionException';

@Injectable({
  providedIn: 'root'
})
export class DashboardHubService extends BaseHubService {
    private static HUB_ROUTE: string = "/dashboardHub";

    private errorCountUpdated = new Subject<ErrorCountsByCategory>;
    private errorCountInitialized = new Subject<ErrorCountsByCategory>;

    protected override getUrl(): string {
        return environment.clickkerWatcher.webSocket.url + DashboardHubService.HUB_ROUTE;
    }

    protected override registerListeners() {
        this.hubConnection?.on("GetInitialReport", (errorCountsByCategory: ErrorCountsByCategoryApi) => {
            const model = new ErrorCountsByCategory(errorCountsByCategory.amountOfFunctionalErrors, errorCountsByCategory.amountOfTechnicalErrors);
            this.errorCountInitialized.next(model);
        });

        this.hubConnection?.on("ErrorCountUpdated", (errorCountsByCategory: ErrorCountsByCategoryApi) => {
            const model = new ErrorCountsByCategory(errorCountsByCategory.amountOfFunctionalErrors, errorCountsByCategory.amountOfTechnicalErrors);
            this.errorCountUpdated.next(model);
        })
    }

    public errorCountIsUpdated(): Observable<ErrorCountsByCategory> {
        return this.errorCountUpdated.asObservable().pipe(
            debounceTime(10000)
        );
    }

    public errorCountIsInitialized(): Observable<ErrorCountsByCategory> {
        return this.errorCountInitialized.asObservable();
    }

    public async getTechnicalErrorChartData(errorChartState: ErrorChartState) {
        if (!this.hubConnection)
            throw new WebsocketConnectionException();

        const technicalData: ErrorChartDataApi = await this.hubConnection.invoke("GetTechnicalChartData", errorChartState.month, errorChartState.week);
        return this.mapErrorChartData(technicalData);
    }

    public async getFunctionalErrorChartData(errorChartState: ErrorChartState) {
        if (!this.hubConnection)
            throw new WebsocketConnectionException();

        const functionalData: ErrorChartDataApi = await this.hubConnection.invoke("GetFunctionalChartData", errorChartState.month, errorChartState.week);
        return this.mapErrorChartData(functionalData);
    }

    private mapErrorChartData(model: ErrorChartDataApi) {
        const labels: ErrorChartLabel[] = model.labels.map(label => new ErrorChartLabel(label.displayValue, label.value));
        const datasets: ErrorDataset[] = model.datasets.map(dataset => new ErrorDataset(dataset.data, dataset.type));
        return new ErrorChartData(labels, datasets)
    }
}
